import { type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent, useCallback, useState } from 'react';
import type { DragType, RangeDragStart, UseSliderDragProps } from '../types';
import { useUnifiedDrag } from './use-unified-drag';

/**
 * 滑块拖拽逻辑 Hook
 * 处理双滑块的拖拽交互，包括单个滑块拖拽和范围拖拽
 * 支持鼠标和触摸事件
 *
 * @param props - Hook 配置参数
 * @returns 拖拽相关的状态和事件处理函数
 */
export const useSliderDrag = ({
  /** 是否禁用拖拽 */
  disabled,
  /** 当前滑块值 [左值, 右值] */
  currentValue,
  /** 最小值 */
  min,
  /** 最大值 */
  max,
  /** 滑块容器引用 */
  sliderRef,
  getValueFromPosition,
  updateValue,
}: UseSliderDragProps) => {
  /** 当前拖拽状态：null | 0 | 1 | 'range' */
  const [isDragging, setIsDragging] = useState<DragType>(null);

  const { startDrag } = useUnifiedDrag({ passiveTouchMove: false });

  /**
   * 重置所有拖拽相关的状态
   */
  const cleanupDrag = () => {
    setIsDragging(null);
  };

  // 单个滑块拖拽处理
  const handleThumbDrag = useCallback(
    (thumbIndex: number, clientX: number) => {
      const newValue = getValueFromPosition(clientX);
      const [leftValue, rightValue] = currentValue;

      if (thumbIndex === 0) {
        // 左滑块：确保不超过右滑块的位置
        const constrainedValue = Math.min(newValue, rightValue);
        updateValue([constrainedValue, rightValue]);
      } else {
        // 右滑块：确保不小于左滑块的位置
        const constrainedValue = Math.max(newValue, leftValue);
        updateValue([leftValue, constrainedValue]);
      }
    },
    [currentValue, getValueFromPosition, updateValue],
  );

  /**
   * 范围拖拽处理
   * 同时移动两个滑块，保持它们之间的距离不变
   *
   * @param clientX - 当前鼠标/触摸的 X 坐标
   * @param dragStart - 拖拽开始时的状态信息
   */
  const handleRangeDrag = (clientX: number, dragStart: RangeDragStart) => {
    if (!sliderRef.current) return;

    // 获取滑块容器的边界信息
    const rect = sliderRef.current.getBoundingClientRect();
    // 计算鼠标移动的距离
    const deltaX = clientX - dragStart.startX;
    // 将像素距离转换为数值距离
    const deltaValue = (deltaX / rect.width) * (max - min);

    // 计算新的左右值
    let newLeft = dragStart.initialLeft + deltaValue;
    let newRight = dragStart.initialRight + deltaValue;

    // 确保范围不超出边界，同时保持原有的间隔宽度
    const rangeWidth = newRight - newLeft;

    // 如果左边界超出最小值，调整整个范围
    if (newLeft < min) {
      newLeft = min;
      newRight = newLeft + rangeWidth;
    }

    // 如果右边界超出最大值，调整整个范围
    if (newRight > max) {
      newRight = max;
      newLeft = newRight - rangeWidth;
    }

    // 更新滑块值
    updateValue([newLeft, newRight]);
  };

  /**
   * 处理滑块的触摸开始事件
   * 开始单个滑块的触摸拖拽操作
   *
   * @param thumbIndex - 滑块索引 (0: 左滑块, 1: 右滑块)
   * @param event - 触摸事件对象
   */
  const handleTouchStart = useCallback(
    (thumbIndex: number, event: ReactTouchEvent | ReactMouseEvent) => {
      if (disabled) return;
      setIsDragging(thumbIndex);
      startDrag(
        event,
        (clientX) => {
          handleThumbDrag(thumbIndex, clientX);
        },
        () => {
          cleanupDrag();
        },
      );
    },
    [disabled, handleThumbDrag, startDrag],
  );

  /**
   * 处理范围拖拽的触摸开始事件
   * 开始整个范围的触摸拖拽操作，同时移动两个滑块
   *
   * @param event - 触摸事件对象
   */
  const handleRangeTouchStart = useCallback(
    (event: ReactTouchEvent | ReactMouseEvent) => {
      if (disabled) return;

      const clientX = 'touches' in event ? (event.touches[0]?.clientX ?? 0) : (event as ReactMouseEvent).clientX;

      const [leftValue, rightValue] = currentValue;
      const dragStart: RangeDragStart = {
        startX: clientX,
        initialLeft: leftValue,
        initialRight: rightValue,
      };

      setIsDragging('range');
      // 记录拖拽开始

      startDrag(
        event,
        (clientX) => {
          handleRangeDrag(clientX, dragStart);
        },
        () => {
          cleanupDrag();
        },
      );
    },
    [disabled, currentValue, startDrag],
  );

  return {
    /** 当前拖拽状态：null(无拖拽) | 0(左滑块) | 1(右滑块) | 'range'(范围拖拽) */
    isDragging,
    handleTouchStart,
    handleRangeTouchStart,
  };
};
